import { useState, useMemo, useCallback } from 'react'
import { ClubManagementData } from '../hooks/useClubManagementData'
import { ModalLayout } from './layouts'
import type { LayoutProps } from './layouts'

interface ClubManagementLayoutProps {
  clubs: ClubManagementData[]
  totalClubs: number
  selectedClubId: number | null
  onSelectClub: (clubId: number) => void
  onDeselectClub: () => void
}

type SortField = 'name' | 'members' | 'pending' | 'created' | 'restrictions'
type SortOrder = 'asc' | 'desc'

export const ClubManagementLayout = ({
  clubs,
  totalClubs,
  selectedClubId,
  onSelectClub,
  onDeselectClub,
}: ClubManagementLayoutProps) => {
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [showBulkUpload, setShowBulkUpload] = useState(false)

  const sortValue = `${sortField}-${sortOrder}`

  const handleSortChange = useCallback((value: string) => {
    const [field, order] = value.split('-')
    setSortField(field as SortField)
    setSortOrder(order as SortOrder)
  }, [])

  const filteredAndSortedClubs = useMemo(() => {
    const filtered = clubs.filter(
      (club) =>
        club.clubName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        club.shortAddress.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return filtered.sort((a, b) => {
      let aValue: any, bValue: any

      switch (sortField) {
        case 'name':
          aValue = a.clubName.toLowerCase()
          bValue = b.clubName.toLowerCase()
          break
        case 'members':
          aValue = a.clubMemberCount
          bValue = b.clubMemberCount
          break
        case 'pending':
          aValue = a.pendingInvites || 0
          bValue = b.pendingInvites || 0
          break
        case 'created':
          aValue = new Date(a.created || 0).getTime()
          bValue = new Date(b.created || 0).getTime()
          break
        case 'restrictions':
          aValue = a.hasRestrictions ? 1 : 0
          bValue = b.hasRestrictions ? 1 : 0
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [clubs, searchTerm, sortField, sortOrder])

  const layoutProps: LayoutProps = {
    clubs,
    filteredAndSortedClubs,
    totalClubs,
    selectedClubId,
    onSelectClub,
    onDeselectClub,
    searchTerm,
    onSearchChange: setSearchTerm,
    sortValue,
    onSortChange: handleSortChange,
    showBulkUpload,
    onShowBulkUpload: setShowBulkUpload,
  }

  const totalMembers = clubs.reduce((sum, club) => sum + (club.clubMemberCount || 0), 0)
  const totalPending = clubs.reduce((sum, club) => sum + (club.pendingInvites || 0), 0)

  return (
    <div className="flex flex-col gap-y-4 min-h-screen">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-bold text-white">Club Overview</h1>
          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold border border-yellow-400 text-yellow-400 bg-transparent rounded">
            BETA
          </span>
        </div>
        <p className="text-white text-opacity-80 text-sm">
          {totalClubs} club{totalClubs !== 1 ? 's' : ''} &middot; {totalMembers.toLocaleString()} member{totalMembers !== 1 ? 's' : ''}
          {totalPending > 0 && <> &middot; {totalPending} pending</>}
          {filteredAndSortedClubs.length !== totalClubs && <> &middot; {filteredAndSortedClubs.length} shown</>}
        </p>
      </div>

      {/* Performance warning */}
      {totalClubs > 50 && (
        <div className="bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
          <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-amber-800 text-sm">
            You have {totalClubs} clubs. Detailed metadata loads for the first 50 only.
          </p>
        </div>
      )}

      {/* Modal Layout */}
      <ModalLayout {...layoutProps} />
    </div>
  )
}
